import {
  formatAppointmentBookingDate,
  formatAppointmentBookingTimeRange,
} from "@/features/appointments/utils/format-appointment-datetime";
import { isBookedBookingStatus } from "@/features/appointments/utils/appointment-slot";
import type { GetAppointmentBookingResponse } from "@/openapi/generated";

export type StudentAppointmentBookingOption = {
  value: string;
  label: string;
  appointmentId: number;
  bookingId: number;
};

export function getAppointmentIdFromBooking(
  booking: GetAppointmentBookingResponse,
): number | null {
  const appointmentId = Number(booking.appointment?.appointmentId);
  return Number.isFinite(appointmentId) && appointmentId > 0 ? appointmentId : null;
}

/** `name - date and time - location - status` */
export function formatStudentAppointmentBookingLabel(
  booking: GetAppointmentBookingResponse,
): string {
  const name = booking.appointment?.name?.trim();
  const date = formatAppointmentBookingDate(booking.startTime);
  const time = formatAppointmentBookingTimeRange(booking.startTime, booking.endTime);
  const dateAndTime = `${date} · ${time}`;
  const location = booking.classRoom?.name?.trim() || null;
  const status = booking.status?.trim();

  const parts: string[] = [];
  if (name) parts.push(name);
  parts.push(dateAndTime);
  if (location) parts.push(location);
  if (status) parts.push(status);
  return parts.join(" - ");
}

export function getStudentAppointmentBookingOptions(
  bookings: GetAppointmentBookingResponse[] | undefined,
): StudentAppointmentBookingOption[] {
  if (!Array.isArray(bookings)) return [];

  return [...bookings]
    .filter((booking) => isBookedBookingStatus(booking.status))
    .sort(
      (a, b) =>
        new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
    )
    .flatMap((booking) => {
      const appointmentId = getAppointmentIdFromBooking(booking);
      const bookingId = Number(booking.bookingId);
      if (!appointmentId || !Number.isFinite(bookingId)) {
        return [];
      }
      return [
        {
          value: String(bookingId),
          label: formatStudentAppointmentBookingLabel(booking),
          appointmentId,
          bookingId,
        },
      ];
    });
}

export function parseAppointmentBookingIdForApi(
  value: string | null | undefined,
): number | null {
  if (!value?.trim()) return null;
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
}
